/**
 * Shared stock-by-state availability utilities.
 * Used by: PDP component, quickshop, PLP badges, cart auto-remove.
 */

export const PROVINCE_TO_STATES = {
  NSW: ['NSW', 'ACT'],
  VIC: ['VIC'],
  QLD: ['QLD'],
  SA: ['SA', 'NT'],
  TAS: ['TAS'],
  WA: ['WA'],
};

export const STATE_TO_LOCATION_LABEL = {
  NSW: 'ACT / NSW',
  ACT: 'ACT / NSW',
  VIC: 'VIC',
  QLD: 'QLD',
  SA: 'NT / SA',
  NT: 'NT / SA',
  TAS: 'TAS',
  WA: 'WA',
};

export const ALL_DISPLAY_STATES = ['ACT / NSW', 'VIC', 'QLD', 'NT / SA', 'TAS', 'WA'];

export const LABEL_TO_STATE = {
  'ACT / NSW': 'NSW',
  VIC: 'VIC',
  QLD: 'QLD',
  'NT / SA': 'SA',
  TAS: 'TAS',
  WA: 'WA',
};

/**
 * Get the stock map for a specific variant from stockData.
 * Returns { "NSW": true, "VIC": false, ... } or null.
 */
function getVariantStock(stockData, variantId) {
  if (!stockData || !stockData.variants || !variantId) return null;
  return stockData.variants[String(variantId)] || null;
}

/**
 * Check if a variant is available in a given state.
 * Returns true (in stock), false (OOS), or null (no per-state data).
 */
export function isAvailableForState(stockData, variantId, stateCode) {
  if (!stateCode) return null;
  const stock = getVariantStock(stockData, variantId);
  if (!stock || Object.keys(stock).length === 0) return null;
  return !!stock[stateCode];
}

/**
 * Get availability for all 6 warehouse zones for a specific variant.
 * Returns { "ACT / NSW": true/false, "VIC": true/false, ... }
 */
export function getZoneAvailability(stockData, variantId) {
  if (!stockData) return {};
  const zones = {};
  for (const [province, states] of Object.entries(PROVINCE_TO_STATES)) {
    const label = STATE_TO_LOCATION_LABEL[province];
    const available = states.some((s) => isAvailableForState(stockData, variantId, s) === true);
    zones[label] = available;
  }
  return zones;
}

/**
 * Check if any variant of a product is available in the current state.
 * Used by quickshop when no specific variant is selected yet.
 * Returns true (in stock), false (OOS), or null (no data / no state).
 */
export function isAnyVariantAvailableInState(stockData, stateCode) {
  if (!stockData || !stockData.variants || !stateCode) return null;
  const variantIds = Object.keys(stockData.variants);
  if (variantIds.length === 0) return null;
  return variantIds.some((vid) => {
    const stock = stockData.variants[vid];
    return stock && !!stock[stateCode];
  });
}

/**
 * Fetch stock_by_state data for a product via section-rendering API.
 * Returns { handle, variants: { variantId: { state: bool } } } or null.
 */
export async function fetchStockByState(handle) {
  try {
    const resp = await fetch(`/products/${handle}?section_id=api-stock-by-state`);
    if (!resp.ok) return null;
    const html = await resp.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const script = doc.querySelector('[data-stock-by-state]');
    if (!script) return null;
    return JSON.parse(script.textContent);
  } catch (e) {
    // Swallow fetch failures: callers fall back to "no metafield" handling
    // (treats product as available everywhere). Logging here would fire on
    // every transient network blip in production.
    return null;
  }
}
